import root from 'window-or-global';
import { captureException, captureMessage, getCurrentScope, init } from '@sentry/react';
import type { ErrorEvent } from '@sentry/types';
import env from 'env/env';
import { config } from './config';

/**
 * Exceptions to be ignored if the stacktrace includes the matching filename.
 *
 * This extends upon the Sentry config for `denyUrls` (which only considers the
 * last script URL in the stacktrace) and `ignoreErrors` (which is a blanket
 * ignore across all script URLs), neither of which can provide the required
 * level of targeting to ignore some third party errors.
 */
const ignoredExceptionsByStacktraceFilename = [
  /**
   * Pinterest errors due to missing error handling for `fetch()` timeouts.
   *
   * REF: https://waitrose.sentry.io/issues/4557658590/events/f6e8930e3dab4b64808bad092d8a6af4/
   */
  { type: 'TypeError', value: 'Load failed', filename: /s\.pinimg\.com/ },
];

const isIgnoredEvent = (event: ErrorEvent): boolean => {
  const exceptionValues = event.exception?.values;

  if (!exceptionValues) {
    return false;
  }

  for (const exception of exceptionValues) {
    for (const ignoredExceptionByStacktraceFilename of ignoredExceptionsByStacktraceFilename) {
      if (
        ignoredExceptionByStacktraceFilename.type === exception.type &&
        ignoredExceptionByStacktraceFilename.value === exception.value &&
        exception.stacktrace?.frames?.some(
          frame =>
            frame?.filename && ignoredExceptionByStacktraceFilename.filename.test(frame.filename),
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

export function initClient({ sentryContentsquare }: { sentryContentsquare: boolean }) {
  const environment = env.name;

  const beforeSend = (event: ErrorEvent) => {
    if (sentryContentsquare) {
      // REF: https://support.contentsquare.com/hc/en-gb/articles/4411095385106-Sentry
      // Name: Sentry / Contentsquare Integration
      // Version: 1.0.0
      (function csBeforeSend() {
        const eventPayload = { ...event };

        /**
         * Fix error in the Contentsquare Sentry Integration:
         * - https://waitrose.sentry.io/issues/6089489112/events/32fca63811ec45218a47559fbcf19024/
         * - https://waitrose.sentry.io/issues/6080338400/events/be3d5bb9968f495fb12413dedf1384da/
         * - https://waitrose.sentry.io/issues/6080465969/events/b5375b1cbc3d4d3a81e9c111a7ab8eb5/
         */
        eventPayload.breadcrumbs = eventPayload.breadcrumbs || [];

        let disableCallback = false;

        root._uxa = root._uxa || [];

        function csCallback() {
          if (!disableCallback) {
            disableCallback = true;

            root.CS_CONF?.integrations_handler?.sentry?.(eventPayload);
          }
        }

        root._uxa.push(['afterPageView', csCallback]);
      })();
    }

    if (isIgnoredEvent(event)) {
      return null;
    }

    return event;
  };

  init({
    ...config,
    beforeSend,
    environment,
    initialScope: {
      tags: { origin: 'front-end' },
    },
  });

  const sentry = {
    captureException,
    captureMessage,
    getCurrentScope,
  };

  // TODO: What is this for? It feels wrong to call init() twice. It seems to be used only in formbuilder: src/formbuilder/index.js
  root.sentry = function sentryInit() {
    init({
      ...config,
      beforeSend,
      environment,
      initialScope: {
        tags: { origin: 'front-end' },
      },
    });

    return sentry;
  };

  return sentry;
}
